<section class="audit-execution" aria-labelledby="audit-execution-title">
  <div class="app-panel">
    <div class="audit-execution__toolbar">
      <div class="app-panel__header">
        <div class="audit-execution__eyebrow">{{ i18n.t('auditExecution.eyebrow') }}</div>
        <h2 class="app-panel__title" id="audit-execution-title">
          {{ execution()?.audit?.name || i18n.t('auditExecution.title') }}
        </h2>
        <p class="app-panel__meta">
          {{ execution()?.audit?.description || i18n.t('auditExecution.meta') }}
        </p>
      </div>

      <button class="secondary-button" type="button" (click)="close()">
        <span class="fa-solid fa-arrow-left" aria-hidden="true"></span>
        {{ i18n.t('auditExecution.actions.back') }}
      </button>
    </div>

    @if (isLoading()) {
      <div class="app-empty-state audit-execution__placeholder" aria-live="polite">
        <span
          class="fa-solid fa-spinner fa-spin audit-execution__placeholder-icon"
          aria-hidden="true"
        ></span>
        <p>{{ i18n.t('auditExecution.states.loading') }}</p>
      </div>
    } @else if (loadError()) {
      <div class="audit-execution__load-error" role="alert">
        <div class="form-message"><span class="app-message__icon app-message__icon--error fa-solid fa-circle-exclamation" aria-hidden="true"></span> {{ loadError() }}</div>
        <button class="secondary-button" type="button" (click)="retry()">
          <span class="fa-solid fa-rotate-right" aria-hidden="true"></span>
          {{ i18n.t('auditExecution.actions.retry') }}
        </button>
      </div>
    } @else if (execution(); as executionDetail) {
      @if (readOnly()) {
        <div class="form-message" role="status">
          <span class="app-message__icon app-message__icon--info fa-solid fa-eye" aria-hidden="true"></span>
          {{ i18n.t(executionDetail.status === 'completed' ? 'auditExecution.preview.readOnly' : 'auditExecution.preview.plannedReadOnly') }}
        </div>
        @if (executionDetail.completed_executions.length) {
          <label class="app-field audit-execution__history-picker">
            <span class="app-field__label">{{ i18n.t('auditExecution.preview.executionDate') }}</span>
            <select [value]="executionDetail.status === 'completed' ? executionDetail.execution_plan_id : executionDetail.completed_executions[0].execution_plan_id" (change)="openCompletedExecution($any($event.target).value)">
              @for (history of executionDetail.completed_executions; track history.execution_plan_id) {
                <option [value]="history.execution_plan_id">{{ historyDate(history.completed_at) }}{{ history.completed_at_approximate ? ' · ' + i18n.t('dashboard.overview.approximate') : '' }}</option>
              }
            </select>
          </label>
        }
        @if (canRequestUnlock()) {
        <section class="audit-execution__unlock-request">
          <button class="secondary-button" type="button" [disabled]="unlockRequestBusy() || unlockRequestSent()" (click)="toggleUnlockRequest()">
            <span class="fa-solid fa-bell" aria-hidden="true"></span>
            {{ i18n.t(unlockRequestSent() ? 'auditExecution.preview.unlockSent' : 'auditExecution.preview.requestUnlock') }}
          </button>
          @if (unlockRequestOpen()) {
            <label class="app-field">
              <span class="app-field__label">{{ i18n.t('auditUnlock.reason') }}</span>
              <textarea class="app-textarea" maxlength="1000" rows="3" [value]="unlockReason()" (input)="unlockReason.set($any($event.target).value)"></textarea>
            </label>
            <button class="primary-button" type="button" [disabled]="!unlockReason().trim() || unlockRequestBusy()" (click)="requestUnlock()">
              @if (unlockRequestBusy()) { <span class="fa-solid fa-spinner fa-spin" aria-hidden="true"></span> }
              {{ i18n.t('auditUnlock.send') }}
            </button>
          }
          @if (unlockRequestMessage()) { <p class="form-message" role="status">{{ unlockRequestMessage() }}</p> }
        </section>
        }
      }
      @if (activeQuestion(); as current) {
        <nav [attr.aria-label]="i18n.t('auditExecution.navigation.groups')">
          <ol class="app-stepper app-stepper--compact">
            @for (step of groups(); track step.key; let groupIndex = $index) {
              <li>
                <button type="button" class="app-stepper__item app-stepper__item--clickable"
                  [class.app-stepper__item--active]="current.groupIndex === groupIndex"
                  [class.app-stepper__item--complete]="step.answered === step.group.items.length"
                  [attr.aria-current]="current.groupIndex === groupIndex ? 'step' : null"
                  [disabled]="isCompleting()" (click)="goToGroup(groupIndex)">
                  <span class="app-stepper__index">{{ groupIndex + 1 }}</span>
                  <span class="app-stepper__copy">
                    <span class="app-stepper__title">{{ step.group.name }}</span>
                    <span class="app-stepper__meta">{{ step.answered }} / {{ step.group.items.length }} {{ i18n.t('auditExecution.fields.answered') }}</span>
                    @if (executionDetail.checklists.length > 1) { <span class="app-stepper__meta">{{ step.checklist.title }}</span> }
                  </span>
                </button>
              </li>
            }
          </ol>
        </nav>
        <article class="audit-execution__checklist audit-execution__wizard">
          <header class="audit-execution__checklist-header">
            <div>
              <div class="audit-execution__checklist-eyebrow">{{ current.checklist.title }} · {{ i18n.t('auditExecution.fields.version') }} {{ current.checklist.version }}</div>
              <h3>{{ current.group.name }}</h3>
            </div>
            <div class="audit-execution__checklist-progress" aria-live="polite">
              {{ i18n.t('auditExecution.navigation.question') }} {{ questionIndex() + 1 }} / {{ totalItemCount() }}
            </div>
          </header>
          <div class="audit-execution__groups">
            @for (question of questions(); track question.key; let index = $index) {
              @let item = question.item;
              <div class="audit-execution__question" [hidden]="index !== questionIndex()">
                <article
                  class="audit-execution__item"
                  [class.audit-execution__item--missing]="isMissingItem(item.id)"
                  [id]="'audit-execution-item-' + item.id"
                >
                  <div class="audit-execution__item-number" aria-hidden="true">
                    {{ index + 1 }}
                  </div>
                  <div class="audit-execution__item-content">
                    <div class="audit-execution__item-header">
                      <h5 tabindex="-1">{{ item.title }}</h5>
                      <span
                        class="audit-execution__answer-state"
                        [class.audit-execution__answer-state--answered]="
                          isAnswered(item)
                        "
                        [class.audit-execution__answer-state--missing]="
                          isMissingItem(item.id)
                        "
                      >
                        <span
                          [class]="
                            isMissingItem(item.id)
                              ? 'fa-solid fa-circle-exclamation'
                              : isAnswered(item)
                                ? 'fa-solid fa-circle-check'
                                : 'fa-regular fa-circle'
                          "
                          aria-hidden="true"
                        ></span>
                        {{
                          i18n.t(
                            isMissingItem(item.id)
                              ? 'auditExecution.states.incomplete'
                              : isAnswered(item)
                                ? 'auditExecution.states.answered'
                                : 'auditExecution.states.unanswered'
                          )
                        }}
                      </span>
                    </div>
                    <div class="audit-execution__item-meta">
                      <span>{{ getItemTypeLabel(item.item_type) }}</span>
                      <span>
                        {{ i18n.t('auditExecution.fields.maxScore') }}:
                        {{ item.max_score }}
                      </span>
                      @if (item.allow_photo) {
                        <span>
                          <span class="fa-solid fa-camera" aria-hidden="true"></span>
                          {{ i18n.t('auditExecution.fields.photoAllowed') }}
                        </span>
                      }
                    </div>
                    <app-audit-execution-answer
                      [planId]="planId()"
                      [item]="item"
                      [readOnly]="readOnly()"
                      (answerSaved)="handleAnswerSaved($event)"
                      (busyChange)="handleItemBusyChange(item.id, $event)"
                      (errorChange)="handleItemErrorChange(item.id, $event)"
                    />
                  </div>
                </article>
              </div>
            }
          </div>
          <nav class="audit-execution__navigation" [attr.aria-label]="i18n.t('auditExecution.navigation.questions')">
            <button type="button" class="secondary-button" [disabled]="questionIndex() === 0 || isCompleting()" (click)="goToQuestion(questionIndex() - 1)">
              <span class="fa-solid fa-arrow-left" aria-hidden="true"></span> {{ i18n.t('auditExecution.navigation.previous') }}
            </button>
            <span class="audit-execution__navigation-count">{{ current.itemIndex + 1 }} / {{ current.group.items.length }} {{ i18n.t('auditExecution.navigation.inGroup') }}</span>
            @if (!isLastQuestion()) {
              <button type="button" class="primary-button" [disabled]="isCompleting()" (click)="goToQuestion(questionIndex() + 1)">
                {{ i18n.t('auditExecution.navigation.next') }} <span class="fa-solid fa-arrow-right" aria-hidden="true"></span>
              </button>
            }
          </nav>
        </article>
      } @else {
        <div class="app-empty-state audit-execution__placeholder">{{ i18n.t('auditExecution.states.emptyItems') }}</div>
      }

      @if (isLastQuestion() && !readOnly()) {
        <footer class="audit-execution__completion">
          <div class="audit-execution__completion-copy">
            <h3>{{ i18n.t('auditExecution.completion.title') }}</h3>
            <p>{{ i18n.t('auditExecution.completion.meta') }}</p>
            @if (hasPendingItemChanges()) {
              <span class="audit-execution__completion-pending" aria-live="polite">
                <span class="fa-solid fa-spinner fa-spin" aria-hidden="true"></span>
                {{ i18n.t('auditExecution.completion.waitingForSave') }}
              </span>
            }
          </div>
          <button
            class="primary-button audit-execution__complete-button"
            type="button"
            [disabled]="isCompleting() || hasPendingItemChanges() || totalItemCount() === 0"
            (click)="openCompletionDialog()"
          >
            <span class="fa-solid fa-flag-checkered" aria-hidden="true"></span>
            {{ i18n.t('auditExecution.completion.action') }}
          </button>
        </footer>
      }

      @if (completionError()) {
        <div class="form-message audit-execution__completion-error" role="alert"><span class="app-message__icon app-message__icon--error fa-solid fa-circle-exclamation" aria-hidden="true"></span>
          {{ completionError() }}
        </div>
      }
    }
  </div>
</section>

<app-confirmation-dialog
  [isOpen]="completionDialogOpen()"
  [title]="i18n.t('auditExecution.completion.confirmation.title')"
  [message]="i18n.t('auditExecution.completion.confirmation.message')"
  [confirmLabel]="
    isCompleting()
      ? i18n.t('auditExecution.completion.completing')
      : i18n.t('auditExecution.completion.confirmation.confirm')
  "
  [cancelLabel]="i18n.t('common.actions.cancel')"
  [confirmDisabled]="isCompleting()"
  (confirmed)="confirmCompletion()"
  (cancelled)="cancelCompletion()"
/>
